import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useApiConfig } from './ApiProvider';
import { apiFetch } from './api-fetch';
import { orgRoute } from './org-route';
import { useOnboardingState } from '@/screens/SOnboarding/queries';

const DISMISSED_KEY = 'pdm.onboarding.banner.dismissed';

function readDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(DISMISSED_KEY) === '1';
}

/**
 * Persistent banner. Renders only when:
 *   - the wizard has at least one incomplete step, AND
 *   - the wizard hasn't been stamped completed_at, AND
 *   - the tenant has zero assets (so a non-empty tenant doesn't see
 *     a "you have not onboarded" banner above their working dashboard),
 *     AND
 *   - the operator hasn't dismissed it locally.
 *
 * "Dismiss" is sessionStorage-by-tenant rather than localStorage so a
 * banner that was wrongly dismissed reappears on a new browser tab —
 * the cost of "saw the banner once more" beats "operator dismissed it
 * accidentally and never sees the prompt again." Localstorage would
 * also leak across users on a shared machine.
 */
export function OnboardingBanner() {
  const state = useOnboardingState();
  const config = useApiConfig();
  const assetCount = useQuery({
    queryKey: ['assets', 'count'],
    queryFn: async (): Promise<number> => {
      try {
        const res = await apiFetch(config, '/v1/assets');
        if (!res.ok) return 0;
        const body = (await res.json()) as { items?: unknown[] };
        return Array.isArray(body.items) ? body.items.length : 0;
      } catch {
        return 0;
      }
    },
    staleTime: 60_000,
  });
  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed());

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === DISMISSED_KEY) setDismissed(readDismissed());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  if (state.isError) return null;
  const data = state.data;
  if (!data) return null;
  if (data.completed_at) return null;
  const total = data.steps.length;
  const done = data.steps.filter((s) => s.completed).length;
  if (done >= total) return null;

  // Tenant already has assets → onboarding is effectively done even
  // if the wizard's step counters are zero. This is the most common
  // path: customers provisioned before the wizard existed don't need
  // to be nagged with a "0/5 steps complete" banner over a working
  // dashboard.
  if ((assetCount.data ?? 0) > 0) return null;

  if (dismissed) return null;

  const onDismiss = () => {
    window.localStorage.setItem(DISMISSED_KEY, '1');
    setDismissed(true);
  };

  return (
    <div
      role="status"
      className="flex items-center justify-between gap-sp-3 border-b border-warn bg-warn-bg px-sp-4 py-sp-2 text-fs-meta text-warn-fg"
    >
      <span>
        Onboarding in progress · {done}/{total} steps complete
      </span>
      <div className="flex items-center gap-sp-3">
        <Link
          to={orgRoute('/onboarding')}
          className="font-medium underline hover:text-gray-900"
        >
          Continue setup →
        </Link>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss onboarding banner"
          className="font-mono text-fs-meta text-warn-fg hover:text-gray-900"
          title="Dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
}
